import React from "react";
import { Link, Navigate } from "react-router-dom";
import { Wrench, Flame, Atom, ArrowRight, Trophy, Clock, Gift, Lock, UserPlus, LogIn } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";

const TR = {
  sk: {
    eyebrow: "Lead magnet",
    title: "Tigik Kvíz Trilógia",
    subtitle: "Otestuj sa za 60 sekúnd v 3 disciplínach a získaj 7 dní ACTIVE grátis 🎁",
    feat1: "10 otázok náhodne z poolu 30+",
    feat2: "Vyhodnotenie s vysvetlivkami",
    feat3: "7-dňový ACTIVE bonus za dokončenie",
    start: "Spustiť",
    bestFor: "Pre",
    gateTitle: "Kvíz je len pre registrovaných",
    gateBody: "Tigik Kvíz je úplne zdarma, ale len pre prihlásených používateľov. Zaregistruj sa (60 sekúnd) a otestuj sa.",
    gateCtaRegister: "Registrovať zadarmo",
    gateCtaLogin: "Mám účet — prihlásiť",
    free: "Zadarmo",
  },
  en: {
    eyebrow: "Lead magnet",
    title: "Tigik Quiz Trilogy",
    subtitle: "Test yourself in 60 seconds across 3 disciplines and earn 7 days of ACTIVE 🎁",
    feat1: "10 random questions from a 30+ pool",
    feat2: "Scored review with explanations",
    feat3: "7-day ACTIVE bonus on completion",
    start: "Start",
    bestFor: "For",
    gateTitle: "Quiz is for registered users only",
    gateBody: "Tigik Quiz is completely free — but only for signed-in users. Sign up (60 seconds) and test yourself.",
    gateCtaRegister: "Sign up — free",
    gateCtaLogin: "I have an account — Sign in",
    free: "Free",
  },
  cs: {
    eyebrow: "Lead magnet",
    title: "Tigik kvíz trilogie",
    subtitle: "Otestuj se za 60 sekund ve 3 disciplínách a získej 7 dní ACTIVE zdarma 🎁",
    feat1: "10 otázek náhodně z poolu 30+",
    feat2: "Vyhodnocení s vysvětlivkami",
    feat3: "7denní ACTIVE bonus za dokončení",
    start: "Spustit",
    bestFor: "Pro",
    gateTitle: "Kvíz je jen pro registrované",
    gateBody: "Tigik kvíz je úplně zdarma, ale jen pro přihlášené uživatele. Zaregistruj se (60 sekund) a otestuj se.",
    gateCtaRegister: "Registrovat zdarma",
    gateCtaLogin: "Mám účet — přihlásit",
    free: "Zdarma",
  },
  de: {
    eyebrow: "Lead magnet",
    title: "Tigik Quiz-Trilogie",
    subtitle: "Teste dich in 60 Sekunden in 3 Disziplinen und sichere dir 7 Tage ACTIVE 🎁",
    feat1: "10 zufällige Fragen aus einem 30+ Pool",
    feat2: "Auswertung mit Erklärungen",
    feat3: "7-Tage-ACTIVE-Bonus bei Abschluss",
    start: "Starten",
    bestFor: "Für",
    gateTitle: "Quiz nur für registrierte Nutzer",
    gateBody: "Das Tigik Quiz ist komplett kostenlos — aber nur für eingeloggte Nutzer. Registriere dich (60 Sekunden) und teste dich.",
    gateCtaRegister: "Kostenlos registrieren",
    gateCtaLogin: "Ich habe ein Konto — Anmelden",
    free: "Kostenlos",
  },
  pl: {
    eyebrow: "Lead magnet",
    title: "Trylogia quizów Tigik",
    subtitle: "Sprawdź się w 60 sekund w 3 dyscyplinach i odbierz 7 dni ACTIVE 🎁",
    feat1: "10 losowych pytań z puli 30+",
    feat2: "Wynik z wyjaśnieniami",
    feat3: "7-dniowy bonus ACTIVE za ukończenie",
    start: "Start",
    bestFor: "Dla",
    gateTitle: "Quiz tylko dla zarejestrowanych",
    gateBody: "Quiz Tigik jest całkowicie darmowy — ale tylko dla zalogowanych użytkowników. Zarejestruj się (60 sekund) i sprawdź się.",
    gateCtaRegister: "Zarejestruj — bezpłatnie",
    gateCtaLogin: "Mam konto — Zaloguj",
    free: "Bezpłatnie",
  },
  fr: {
    eyebrow: "Lead magnet",
    title: "Tigik Quiz Trilogie",
    subtitle: "Teste-toi en 60 secondes sur 3 disciplines et obtiens 7 jours ACTIVE gratuits 🎁",
    feat1: "10 questions aléatoires parmi un pool de 30+",
    feat2: "Résultats avec explications",
    feat3: "Bonus ACTIVE de 7 jours pour avoir terminé",
    start: "Démarrer",
    bestFor: "Pour",
    gateTitle: "Le quiz est réservé aux membres inscrits",
    gateBody: "Le Tigik Quiz est totalement gratuit, mais uniquement pour les utilisateurs connectés. Inscris-toi (60 secondes) et teste-toi.",
    gateCtaRegister: "S'inscrire gratuitement",
    gateCtaLogin: "J'ai un compte — me connecter",
    free: "Gratuit",
},
};

const ICONS = { Wrench, Flame, Atom };

const PROS = {
  pipefitter: { sk: "potrubárov", en: "pipefitters", cs: "potrubářů", de: "Rohrleger", pl: "rurociągowców" },
  "manual-tig": { sk: "TIG zváračov", en: "TIG welders", cs: "TIG svářečů", de: "WIG-Schweißer", pl: "spawaczy TIG" },
  orbital: { sk: "orbitalistov", en: "orbital welders", cs: "orbitalistů", de: "Orbital-Schweißer", pl: "spawaczy orbitalnych" },
};

const COLOR_CLASSES = {
  amber: { bg: "from-amber-950/40 to-zinc-950", border: "border-amber-700/40", icon: "text-amber-400", btn: "bg-amber-500 hover:bg-amber-400 text-black" },
  cyan: { bg: "from-cyan-950/40 to-zinc-950", border: "border-cyan-700/40", icon: "text-cyan-400", btn: "bg-cyan-500 hover:bg-cyan-400 text-black" },
  violet: { bg: "from-violet-950/40 to-zinc-950", border: "border-violet-700/40", icon: "text-violet-400", btn: "bg-violet-500 hover:bg-violet-400 text-black" },
};

export default function QuizIndex() {
  const { lang } = useLang();
  const { user, loading } = useAuth();
  const t = TR[lang] || TR.sk;
  const [quizzes, setQuizzes] = React.useState([]);

  React.useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quiz/meta`)
      .then((r) => r.json())
      .then((d) => setQuizzes(d.quizzes || []))
      .catch(() => {});
  }, []);

  // Auth gate — Quiz is FREE but only for registered users
  if (!loading && !user) {
    return (
      <div className="min-h-screen bg-black text-zinc-200" data-testid="quiz-gate">
        <div className="max-w-2xl mx-auto px-6 py-20">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold-400 mb-3 flex items-center gap-2">
            <Trophy className="w-3 h-3" /> {t.eyebrow}
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl text-white leading-[1.05] mb-5">
            {t.title}
          </h1>
          <div className="border border-gold-700/40 bg-gradient-to-br from-gold-950/40 to-zinc-950 p-7 mt-6">
            <Lock className="w-10 h-10 text-gold-400 mb-5" />
            <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 border border-gold-700/40 text-gold-300 text-[10px] font-mono uppercase tracking-[0.25em]">
              <Gift className="w-3 h-3" /> {t.free}
            </div>
            <h2 className="font-display font-bold text-2xl text-white mb-3">{t.gateTitle}</h2>
            <p className="text-sm text-zinc-400 leading-relaxed mb-6">{t.gateBody}</p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-5 py-3 bg-gold-500 hover:bg-gold-400 text-black font-mono uppercase tracking-wider text-xs font-bold transition-colors"
                data-testid="quiz-gate-register"
              >
                <UserPlus className="w-3 h-3" /> {t.gateCtaRegister}
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 px-5 py-3 border border-gold-700/40 text-gold-300 hover:bg-gold-900/20 font-mono uppercase tracking-wider text-xs"
                data-testid="quiz-gate-login"
              >
                <LogIn className="w-3 h-3" /> {t.gateCtaLogin}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-zinc-200" data-testid="quiz-index">
      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold-400 mb-3 flex items-center gap-2">
          <Trophy className="w-3 h-3" /> {t.eyebrow}
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] mb-4">
          {t.title}
        </h1>
        <p className="text-base text-zinc-400 max-w-2xl">{t.subtitle}</p>

        <div className="mt-6 flex flex-wrap gap-4 text-xs font-mono uppercase tracking-wider text-zinc-500">
          <span className="inline-flex items-center gap-1.5"><Clock className="w-3 h-3 text-gold-400" /> {t.feat1}</span>
          <span className="text-zinc-800">·</span>
          <span className="inline-flex items-center gap-1.5"><Trophy className="w-3 h-3 text-gold-400" /> {t.feat2}</span>
          <span className="text-zinc-800">·</span>
          <span className="inline-flex items-center gap-1.5"><Gift className="w-3 h-3 text-gold-400" /> {t.feat3}</span>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {quizzes.map((q) => {
            const Icon = ICONS[q.icon] || Trophy;
            const c = COLOR_CLASSES[q.color] || COLOR_CLASSES.amber;
            const titleKey = `title_${lang}`;
            const descKey = `desc_${lang}`;
            return (
              <Link
                key={q.id}
                to={`/quiz/${q.id}`}
                data-testid={`quiz-card-${q.id}`}
                className={`group relative overflow-hidden border ${c.border} bg-gradient-to-br ${c.bg} p-7 transition-all hover:scale-[1.02] hover:shadow-2xl`}
              >
                <Icon className={`w-10 h-10 ${c.icon} mb-5`} />
                <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500 mb-2">
                  {t.bestFor} {PROS[q.id]?.[lang] || PROS[q.id]?.sk}
                </div>
                <h3 className="font-display font-bold text-2xl text-white mb-3">{q[titleKey] || q.title_sk}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed mb-6">{q[descKey] || q.desc_sk}</p>
                <div className={`inline-flex items-center gap-2 px-4 py-2 ${c.btn} font-mono uppercase tracking-wider text-xs font-bold transition-colors`}>
                  {t.start} <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
